import { type FC, useEffect, useMemo } from 'react';
import { usePlayerChart } from '../context';
import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';

const TimelinePlayer: FC<{ onTimestampClick?: (payload: { timestamp: number; values: Array<{ name: string; paramId?: string; value: number | null }> }) => void }> = ({ onTimestampClick }) => {
	const {
		currentTimestamp,
		minTimestamp,
		maxTimestamp,
		setCurrentIndex,
		uniqueTimestamps,
		originalTimestamps,
		handleStop,
		isLiveMode,
		config, // get full chartData from context
		isPlaying, // read playing state
		requestEvents,
	} = usePlayerChart();

	const [isDragging, setIsDragging] = useState(false);
	const [tooltipData, setTooltipData] = useState<{
		visible: boolean;
		x: number;
		y: number;
		content: {
			timestamp: number;
			requestData: Array<{
				infrastructureId: number;
				nameInfrastructure: string;
				mod_value: number;
			}>;
		} | null;
	}>({ visible: false, x: 0, y: 0, content: null });
	const timelineRef = useRef<HTMLDivElement>(null);

	const totalMinutes = useMemo(() => {
		return Math.ceil((maxTimestamp - minTimestamp) / (1000 * 60));
	}, [maxTimestamp, minTimestamp]);

	const timeMarks = useMemo(() => {
		const labelMarks: Array<{
			minutes: number;
			percentage: number;
			label: string;
		}> = [];

		const clickableMarks: Array<{
			dataIndex: number;
			percentage: number;
			timestamp: number;
			isVirtual?: boolean;
		}> = [];

		const totalHours = totalMinutes / 60;
		const totalDays = totalHours / 24;

		let labelInterval: number;

		if (totalDays > 7) {
			labelInterval = 12 * 60;
		} else if (totalDays > 3) {
			labelInterval = 6 * 60;
		} else if (totalHours > 12) {
			labelInterval = 3 * 60;
		} else if (totalHours > 6) {
			labelInterval = 2 * 60;
		} else if (totalHours > 3) {
			labelInterval = 60;
		} else {
			labelInterval = 30;
		}

		for (let i = 0; i <= totalMinutes; i += labelInterval) {
			const percentage = (i / totalMinutes) * 100;
			const label = `${i}m`;

			labelMarks.push({
				minutes: i,
				percentage,
				label,
			});
		}

		if (labelMarks[labelMarks.length - 1].minutes !== totalMinutes) {
			const finalLabel = `${totalMinutes}m`;

			labelMarks.push({
				minutes: totalMinutes,
				percentage: 100,
				label: finalLabel,
			});
		}

		uniqueTimestamps.forEach((timestamp, index) => {
			const minutesFromStart = (timestamp - minTimestamp) / (1000 * 60);
			const percentage = (minutesFromStart / totalMinutes) * 100;

			const isVirtualStartPoint =
				index === 0 && timestamp === minTimestamp && !originalTimestamps.includes(timestamp);

			const isVirtualEndPoint =
				index === uniqueTimestamps.length - 1 &&
				timestamp === maxTimestamp &&
				!originalTimestamps.includes(timestamp);

			const isVirtual = isVirtualStartPoint || isVirtualEndPoint;

			clickableMarks.push({
				dataIndex: index,
				percentage,
				timestamp,
				isVirtual,
			});
		});

		return { labelMarks, clickableMarks };
	}, [totalMinutes, uniqueTimestamps, minTimestamp, maxTimestamp, originalTimestamps]);

	const getCurrentPosition = useCallback(() => {
		if (!currentTimestamp) return 0;
		const currentMinutes = (currentTimestamp - minTimestamp) / (1000 * 60);
		return (currentMinutes / totalMinutes) * 100;
	}, [currentTimestamp, minTimestamp, totalMinutes]);

	const getCurrentDateTime = useCallback(() => {
		if (!currentTimestamp) return { date: '', time: '', isVirtual: false };

		const date = dayjs(currentTimestamp);

		const isVirtualPoint =
			!originalTimestamps.includes(currentTimestamp) &&
			(currentTimestamp === minTimestamp || currentTimestamp === maxTimestamp);

		const formattedDate = date.format('YYYY-MM-DD');
		const formattedTime = date.format('hh:mm:ss A');

		return {
			date: formattedDate,
			time: formattedTime,
			isVirtual: isVirtualPoint,
		};
	}, [currentTimestamp, originalTimestamps, minTimestamp, maxTimestamp]);

	const handleTimelineClick = (event: React.MouseEvent<HTMLDivElement>) => {
		if (isLiveMode) return;

		handleStop();

		const rect = event.currentTarget.getBoundingClientRect();
		const clickX = event.clientX - rect.left;
		const percentage = (clickX / rect.width) * 100;

		const clickTimestamp = minTimestamp + (percentage / 100) * (maxTimestamp - minTimestamp);

		let closestIndex = 0;
		let minDistance = Math.abs(uniqueTimestamps[0] - clickTimestamp);

		for (let i = 1; i < uniqueTimestamps.length; i++) {
			const distance = Math.abs(uniqueTimestamps[i] - clickTimestamp);
			if (distance < minDistance) {
				minDistance = distance;
				closestIndex = i;
			}
		}

		setCurrentIndex(closestIndex);
	};

	const emitValuesForIndex = useCallback(
		(index: number) => {
			const ts = uniqueTimestamps[index];
			if (ts == null || !config?.chartData) return;

			const values = config.chartData.map((result) => {
				const point = result.data.find((p) => p.x === ts);
				return {
					name: result.info.name,
					paramId: result.paramId,
					value: point ? point.y : null,
				};
			});

			onTimestampClick?.({ timestamp: ts, values });
		},
		[uniqueTimestamps, config, onTimestampClick],
	);

	const handleMarkClick = (dataIndex: number) => {
		if (isLiveMode) return;

		handleStop();
		setCurrentIndex(dataIndex);
		emitValuesForIndex(dataIndex);
	};

	const updatePositionFromCoordinates = useCallback(
		(clientX: number) => {
			if (!timelineRef.current) return;

			const rect = timelineRef.current.getBoundingClientRect();
			const clickX = clientX - rect.left;
			const percentage = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
			const dragTimestamp = minTimestamp + (percentage / 100) * (maxTimestamp - minTimestamp);

			let closestIndex = 0;
			let minDistance = Math.abs(uniqueTimestamps[0] - dragTimestamp);

			for (let i = 1; i < uniqueTimestamps.length; i++) {
				const distance = Math.abs(uniqueTimestamps[i] - dragTimestamp);
				if (distance < minDistance) {
					minDistance = distance;
					closestIndex = i;
				}
			}

			setCurrentIndex(closestIndex);
			emitValuesForIndex(closestIndex);
		},
		[minTimestamp, maxTimestamp, uniqueTimestamps, setCurrentIndex, emitValuesForIndex],
	);

	const handleMouseDown = (event: React.MouseEvent) => {
		if (isLiveMode) return;

		event.preventDefault();

		handleStop();
		setIsDragging(true);
		updatePositionFromCoordinates(event.clientX);
	};

	const handleMouseMove = useCallback(
		(event: MouseEvent) => {
			if (isDragging) {
				updatePositionFromCoordinates(event.clientX);
			}
		},
		[isDragging, updatePositionFromCoordinates],
	);

	const handleMouseUp = useCallback(() => {
		setIsDragging(false);
	}, []);

	// Emit values on each playback tick (when currentTimestamp advances during play)
	useEffect(() => {
		if (isLiveMode) return;
		if (!isPlaying) return;
		if (currentTimestamp == null) return;

		const idx = uniqueTimestamps.indexOf(currentTimestamp);
		if (idx >= 0) {
			emitValuesForIndex(idx);
		}
	}, [isPlaying, currentTimestamp, uniqueTimestamps, emitValuesForIndex, isLiveMode]);


	useEffect(() => {
		if (isDragging) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
			document.body.style.cursor = 'grabbing';
			document.body.style.userSelect = 'none';
		} else {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		}

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};
	}, [isDragging, handleMouseMove, handleMouseUp]);

	const { labelMarks, clickableMarks } = timeMarks;
	const currentPosition = getCurrentPosition();
	const { date, time, isVirtual } = getCurrentDateTime();

	return (
		<div className="w-full">
			<div
				className="relative pb-2"
				style={{ marginLeft: '110px', marginRight: '80px' }}
				ref={timelineRef}
			>
				<div
					className={`absolute inset-0 ${isLiveMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}
					onClick={handleTimelineClick}
					style={{ paddingTop: '16px', paddingBottom: 'px' }}
				/>

				<div
					className={`relative h-1 bg-gray-300 rounded-full transition-colors duration-200 pointer-events-none ${
						isLiveMode ? 'opacity-60' : 'hover:bg-gray-400'
					}`}
				>
					<div
						className="absolute top-0 left-0 h-full bg-[#22a2ca] transition-all duration-300 rounded-full"
						style={{ width: `${currentPosition}%` }}
					/>
				</div>

				{labelMarks.map((mark) => (
					<div
						key={`label-${mark.minutes}-${mark.percentage}`}
						className="absolute flex flex-col items-center pointer-events-none"
						style={{
							left: `${mark.percentage}%`,
							transform: 'translateX(-50%)',
							top: '-25px',
						}}
					>
						<div>
							<span className="text-xs text-gray-400 font-medium whitespace-nowrap">
								{mark.label}
							</span>
						</div>
					</div>
				))}

				{clickableMarks.map((mark) => {
					
					const hasRequestData = requestEvents.some(event => 
						Math.abs(event.timestamp - mark.timestamp) < 1000
					);

					return (
						<div
							key={`clickable-${mark.dataIndex}-${mark.timestamp}`}
							className={`absolute flex flex-col items-center z-30 ${
								isLiveMode ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:scale-125'
							} transition-all duration-200 group`}
							style={{
								left: `${mark.percentage}%`,
								transform: 'translateX(-50%)',
								top: '-3px',
							}}
							onClick={() => handleMarkClick(mark.dataIndex)}
							title={
								mark.isVirtual
									? `${mark.dataIndex === 0 ? 'Start point' : 'End point'}: ${dayjs(mark.timestamp).format('hh:mm:ss A')}`
									: `Point ${mark.dataIndex + 1}: ${dayjs(mark.timestamp).format('hh:mm:ss A')}${hasRequestData ? ' - Has event data' : ''}`
							}
						>
							<div
								className={`w-3 h-3 rounded-full shadow-md hover:shadow-lg transition-all duration-200 border-2 ${
									hasRequestData 
										? 'bg-[#00203C] hover:bg-gray-800 border-white hover:border-gray-200'
										: mark.isVirtual
											? 'bg-[#22a2ca] hover:bg-[#078cb3] border-[#0891b2] hover:border-[#0c7c8e]'
											: 'bg-[#ccf1ff] hover:bg-[#078cb3] border-[#aae6fe] hover:border-[#078cb3]'
								}`}
							/>
						</div>
					);
				})}

				{requestEvents.map((event) => {
					const eventMinutes = (event.timestamp - minTimestamp) / (1000 * 60);
					const eventPercentage = (eventMinutes / totalMinutes) * 100;
					
					return (
						<div
							key={`request-event-${event.timestamp}-${event.timeEvent}`}
							className="absolute flex flex-col items-center z-50 cursor-pointer hover:scale-125 transition-all duration-200"
							style={{
								left: `${eventPercentage}%`,
								transform: 'translateX(-50%)',
								top: '-3px',
							}}
							onClick={() => {
								if (isLiveMode) return;
								
								handleStop();
								

								let closestIndex = 0;
								let minDistance = Math.abs(uniqueTimestamps[0] - event.timestamp);
								
								for (let i = 1; i < uniqueTimestamps.length; i++) {
									const distance = Math.abs(uniqueTimestamps[i] - event.timestamp);
									if (distance < minDistance) {
										minDistance = distance;
										closestIndex = i;
									}
								}
								
								setCurrentIndex(closestIndex);
							}}
							onMouseEnter={(e) => {
								const rect = e.currentTarget.getBoundingClientRect();
								const pointRect = e.currentTarget.querySelector('div')?.getBoundingClientRect();
								const centerX = pointRect ? pointRect.left + pointRect.width / 2 : rect.left + rect.width / 2;
								setTooltipData({
									visible: true,
									x: centerX,
									y: rect.top - 10,
									content: {
										timestamp: event.timestamp,
										requestData: event.requestData
									}
								});
							}}
							onMouseLeave={() => {
								setTooltipData(prev => ({ ...prev, visible: false }));
							}}
						>
							<div className="w-3 h-3 rounded-full bg-[#00203C] border-2 border-white shadow-lg hover:shadow-xl transition-all duration-200" />
						</div>
					);
				})}

				<div
					className="absolute flex flex-col items-center z-40 pointer-events-none"
					style={{
						left: `${currentPosition}%`,
						transform: 'translateX(-50%)',
						top: '-70px',
					}}
				>
					<div
						className={`${
							isVirtual ? 'bg-[#22a2ca] hover:bg-[#078cb3]' : 'bg-[#22a2ca] hover:bg-[#078cb3]'
						} text-white px-3 py-2 rounded-md shadow-lg text-center text-sm font-medium relative mb-1 ${
							isLiveMode ? 'cursor-not-allowed' : isDragging ? 'cursor-grabbing' : 'cursor-grab'
						} select-none transition-colors duration-200 pointer-events-auto`}
						onMouseDown={handleMouseDown}
					>
						<div className="whitespace-nowrap">
							<div className="text-xs font-semibold">{date}</div>
							<div className="text-xs">{time}</div>
						</div>

						<div className="absolute top-full left-1/2 transform -translate-x-1/2 pointer-events-none">
							<div
								className={`w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent ${
									isVirtual ? 'border-t-[#22a2ca]' : 'border-t-[#22a2ca]'
								}`}
							/>
						</div>
					</div>

					<div
						className={`w-4 h-4 bg-[#22a2ca] rounded-full border-2 border-white shadow-lg ${
							isLiveMode ? 'cursor-not-allowed' : isDragging ? 'cursor-grabbing' : 'cursor-grab'
						} hover:bg-[#078cb3] transition-colors duration-200 pointer-events-auto`}
						style={{ marginTop: '8px' }}
						onMouseDown={handleMouseDown}
					/>
				</div>
			</div>
			
			{tooltipData.visible && tooltipData.content && createPortal(
				<div 
					className="fixed bg-[#00203C] text-white px-2 py-1 rounded text-xs whitespace-nowrap transition-opacity duration-200 pointer-events-none shadow-lg"
					style={{
						left: tooltipData.x,
						top: tooltipData.y,
						transform: 'translateX(-50%) translateY(-100%) translateY(-8px)',
						zIndex: 99999,
					}}
				>
					<div className="text-xs">{dayjs(tooltipData.content.timestamp).format('hh:mm:ss A')}</div>
					{tooltipData.content.requestData.length > 0 && (
						<div className="text-xs mt-1">
							{tooltipData.content.requestData.map((req) => (
								<div key={`req-${req.infrastructureId}-${req.mod_value}`}>
									Asset {req.nameInfrastructure} to {req.mod_value}
								</div>
							))}
						</div>
					)}
					
					<div className="absolute top-full left-1/2 transform -translate-x-1/2">
						<div className="w-0 h-0 border-l-2 border-r-2 border-t-2 border-l-transparent border-r-transparent border-t-red-500" />
					</div>
				</div>,
				document.body
			)}
		</div>
	);
};

export default TimelinePlayer;
